/* eslint-disable react/display-name */
import * as React from "react";
import {
  makeStyles,
  shorthands,
  Tab,
  TabList,
  SelectTabData,
  SelectTabEvent,
  TabValue,
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Button,
} from "@fluentui/react-components";

const useStyles = makeStyles({
  root: {
    alignItems: "flex-start",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    ...shorthands.padding("50px", "20px"),
    rowGap: "20px",
    fontFamily: "ShentoxMedium",
  },
  TabList: {
    fontFamily: "ShentoxMedium",
    "& button span": {
      fontFamily: "ShentoxMedium",
    },
  },
  panels: {
    ...shorthands.padding(0, "10px"),
    fontFamily: "ShentoxMedium",
    overflowX: "hidden",
  },
  InnerPanel: {
    fontFamily: "ShentoxRegular",
  },
  ButtonClick: {
    marginTop: "12px",
  },
});

const injectCommandHandler = async (command: any) => {
  if (typeof command !== "string") {
    return;
  }
  await injectCommand(command);
  return;
};

const injectCommand = async (command: any) => {
  const namespace =
    window.location.host.startsWith("localhost") || window.location.host.startsWith("exceldevstc")
      ? window.location.host.startsWith("localhost")
        ? "=EVEONLINEDEV" /* Dev */
        : "=EVEONLINETEST" /* Test */
      : "=EVEONLINE"; /* Prod */
  try {
    await Excel.run(async (context) => {
      const range = context.workbook.getSelectedRange();
      // Read the range address
      range.load("address");
      let pretty = namespace + command;
      if (command == "market") pretty = namespace + ".MARKET_ORDERS(10000002, 999)";
      else if (command == "market_streaming") pretty = namespace + ".MARKET_ORDERS_STREAMING(10000002, 999)";
      else if (command == "invsearch") pretty = namespace + '.INVENTORYSEARCH("Rifter")';
      else if (command == "single_war") pretty = namespace + ".WAR(730339)";
      else if (command == "searchsystem") pretty = namespace + '.SEARCHSOLARSYSTEM("Peri")';
      else if (command == "char_assets") pretty = namespace + ".CHARACTER_ASSETS(,,,1)";
      else if (command == "markethistory") pretty = namespace + ".MARKET_HISTORY(10000002, 587)";
      else if (command == "markethistory30days") pretty = namespace + ".MARKET_HISTORY(10000002, 587, 30)";
      else if (command == "marketstats") pretty = namespace + ".MARKET_ORDERS_STATS(10000002, 587)";
      else if (command == "structuresearch") pretty = namespace + '.STRUCTURESEARCH("Tranquility")';
      else if (command == "searchstations") pretty = namespace + '.SEARCHSTATIONS("Gurista")';
      range.values = [[pretty]];

      await context.sync();
    });
  } catch (error) {
    console.log(error);
  }
};

export const CommandsList = () => {
  const styles = useStyles();
  const [selectedValue, setSelectedValue] = React.useState<TabValue>("public");
  const onTabSelect = (_event: SelectTabEvent, data: SelectTabData) => {
    setSelectedValue(data.value);
  };
  const publicData = [
    {
      title: ".INVENTORYSEARCH",
      text: "Returns items matching the search string from static data files. If an empty string is input it will return all items (Desktop only)",
      command: "invsearch",
    },
    {
      title: ".CHARACTER",
      text: "Returns public information about the character id, optional second boolean parameter to fetch location and ship if the character is logged in.",
      command: ".CHARACTER(90264928)",
    },
    {
      title: ".ALLIANCE",
      text: "Returns the public information for the given alliance id",
      command: ".ALLIANCE(434243723)",
    },
    {
      title: ".BLUEPRINT",
      text: "Returns the blueprint for the type id or blueprint id given.",
      command: ".BLUEPRINT(999)",
    },
    {
      title: ".BLUEPRINT_EXPLOSION",
      text: "Experimental! Returns the blueprint nested information for production for the type id or blueprint id given, can be an array for types or blueprints. The quantity array can be set to adjust the quantities to manufacture. Doesn't account for bonuses and in complicated blueprints can be less accurate",
      command: ".BLUEPRINT_EXPLOSION(999)",
    },
    {
      title: ".CORPORATION",
      text: "Returns the public information for the given corporation id",
      command: ".CORPORATION(109299958)",
    },
    {
      title: ".ROUTE",
      text: "Autopilot route based on solarsystem ids, optional safety flags 'shortest','secure','unsecure'. Default is 'shortest'",
      command: ".ROUTE(30000142,30002187)",
    },
    {
      title: ".TYPE",
      text: "Returns type information for the given type id",
      command: ".TYPE(645)",
    },
    {
      title: ".GROUP",
      text: "Returns group information based on group id.",
      command: ".GROUP(27)",
    },
    {
      title: ".CATEGORY",
      text: "Returns category information based on category id.",
      command: ".CATEGORY(6)",
    },
    {
      title: ".SCHEMATIC",
      text: "Returns PI schematic data by schematic_id or by end product type_id",
      command: ".SCHEMATIC(9848)",
    },
    {
      title: ".INDUSTRYINDEX",
      text: "Returns indicies for the given solarsystem id, or a list of all indicies if id is ommitted.",
      command: ".INDUSTRYINDEX(30000142)",
    },
    {
      title: ".REGION",
      text: "Returns region information for the given id.",
      command: ".REGION(10000001)",
    },
    {
      title: ".SEARCHREGION",
      text: "Returns region information based on the search string. Empty string will return all regions",
      command: ".SEARCHREGION()",
    },
    {
      title: ".SEARCHSOLARSYSTEM",
      text: "Returns solar system information based on the search string. Empty string will return all systems.",
      command: "searchsystem",
    },
    {
      title: ".SOLARSYSTEM",
      text: "Returns solar system information for the given id.",
      command: ".SOLARSYSTEM(30000142)",
    },
    {
      title: ".SEARCHSTATIONS",
      text: "Returns stations matching a given search string.",
      command: "searchstations",
    },
    {
      title: ".STATION",
      text: "Returns station information for the given station id",
      command: ".STATION(60003760)",
    },
    {
      title: ".MARKET_ORDERS_STATS",
      text: "Returns public market order stats, median, 5% percentile order price etc. based on available orders in the region.",
      command: "marketstats",
    },
    {
      title: ".MARKET_ORDERS",
      text: "Returns the market orders in the given region id for the type id. In stations and public structures. Private structures are a seperate function.",
      command: "market",
    },
    {
      title: ".MARKET_ORDERS_STREAMING",
      text: "Returns the market orders in the given region id for the type id. Automatically updates every 1 hour",
      command: "market_streaming",
    },
    {
      title: ".MARKET_HISTORY",
      text: "Returns the market history in the given region id for the type id. Limited to 300 calls per minute",
      command: "markethistory",
    },
    {
      title: ".MARKET_HISTORY aggregates over X days",
      text: "Returns the market history aggregates for the past X days, in the given region id for the type id. Limited to 300 calls per minute",
      command: "markethistory30days",
    },
    {
      title: ".WARS",
      text: "Returns a list of the 50 latest wars. (Note: this takes a long time to fetch nested info)",
      command: ".WARS()",
    },
    {
      title: ".WAR",
      text: "Returns war information for the given war id",
      command: "single_war",
    },
  ];
  const characterData = [
    {
      title: ".ACTIVE_CHARACTER",
      text: "Returns all currently signed in characters. You can use them as cell references in other character and corporation functions.",
      command: ".ACTIVE_CHARACTER()",
    },
    {
      title: ".CHARACTER_ATTRIBUTES",
      text: "Returns a card containing attributes of the character id or entity given. Returns the first character if it is used without a character id or entity entered",
      command: ".CHARACTER_ATTRIBUTES()",
    },
    {
      title: ".CHARACTER_ASSETS",
      text: "Returns the list of assets for the character id or entity given. If this function is run without a character id, it will return assets for all signed in characters. The second parameter is for filtering by name of the item.",
      command: "char_assets",
    },
    {
      title: ".CHARACTER_CLONES",
      text: "Returns the jump clones of a character. If this function is run without a character id, it will return clones for all signed in characters.",
      command: ".CHARACTER_CLONES()",
    },
    {
      title: ".CHARACTER_CONTRACTS",
      text: "Returns the contracts of a character. 500 per page. Requires selecting the character_id_or_entity by the user. Has optional to help filter data by title/status. Hides deleted contracts by default(include_deleted parameter)",
    },
    {
      title: ".CHARACTER_CORPORATION_ROLES",
      text: "Returns the corporation roles of a character. If this function is run without a character id, it will return corporation role cards for all signed in characters.",
      command: ".CHARACTER_CORPORATION_ROLES()",
    },
    {
      title: ".CHARACTER_FITTINGS",
      text: "Returns the saved fittings of a character. If this function is run without a character id, it will return fittings for all signed in characters.",
      command: ".CHARACTER_FITTINGS()",
    },
    {
      title: ".CHARACTER_IMPLANTS",
      text: "Returns the implants of a character active clone. If this function is run without a character id, it will return implants for all signed in characters.",
      command: ".CHARACTER_IMPLANTS()",
    },
    {
      title: ".CHARACTER_MINING",
      text: "Returns the mining activity of a character. If this function is run without a character id, it will return mining activity the first signed in character.",
      command: ".CHARACTER_MINING()",
    },
    {
      title: ".CHARACTER_ORDERS",
      text: "Returns the open market orders for the character id or entity given. If this function is run without a character id, it will return open market orders for all signed in characters.",
      command: ".CHARACTER_ORDERS()",
    },
    {
      title: ".CHARACTER_ORDERS_HISTORY",
      text: "Returns the historical orders for the character id or entity given. If this function is run without a character id, it will return historical orders for the first signed in character.",
      command: ".CHARACTER_ORDERS_HISTORY()",
    },
    {
      title: ".CHARACTER_SKILLS",
      text: "Returns the skills for the character id or entity given. If this function is run without a character id, it will return skills for the first signed in character.",
      command: ".CHARACTER_SKILLS()",
    },
    {
      title: ".CHARACTER_SKILL_QUEUE",
      text: "Returns the skill queue for the character id or entity given. If this function is run without a character id, it will return the skill queue for the first signed in character.",
      command: ".CHARACTER_SKILL_QUEUE()",
    },
    {
      title: ".CHARACTER_STANDINGS",
      text: "Returns the standings for the character id or entity given. If this function is run without a character id, it will return skills for the first signed in character.",
      command: ".CHARACTER_STANDINGS()",
    },
    {
      title: ".CHARACTER_WALLET_TRANSACTIONS",
      text: "Returns the wallet transactions for the character id or entity given. If this function is run without a character id, it will return wallet transactions for all signed in characters.",
      command: ".CHARACTER_WALLET_TRANSACTIONS()",
    },
    {
      title: ".CHARACTER_WALLET_JOURNAL",
      text: "Returns the wallet journal entries for the character id or entity given. If this function is run without a character id, it will return wallet journal entries for all signed in characters.",
      command: ".CHARACTER_WALLET_JOURNAL()",
    },
    {
      title: ".CHARACTER_WALLET_BALANCE",
      text: "Returns the current wallet balance for the character id or entity given. If this function is run without a character id, it will return wallet balance for the first signed in character.",
      command: ".CHARACTER_WALLET_BALANCE()",
    },
    {
      title: ".CHARACTER_LP",
      text: "Returns the LP corporation and the balance for the character id or entity given. Outputs a card for each character if character id is ommited, with the list of corporation and their values as an array",
      command: ".CHARACTER_LP()",
    },
    {
      title: ".CHARACTER_INDUSTRY_JOBS",
      text: "Returns the industry jobs for the character id or entity given. status_filter parameter filters the results by the status of the job (as a string). If this function is run without a character id, it will return industry jobs for all signed in characters.",
      command: ".CHARACTER_INDUSTRY_JOBS()",
    },
    {
      title: ".CHARACTER_BLUEPRINTS",
      text: "Returns the owned blueprints for the character id or entity given. If this function is run without a character id, it will return owned blueprints for all signed in characters.",
      command: ".CHARACTER_BLUEPRINTS()",
    },
    {
      title: ".STRUCTURE",
      text: "Returns structure information for a given id, requires a logged in character as it fetches data from ESI",
    },
    {
      title: ".STRUCTURESEARCH",
      text: "Returns structure information for a given text string, requires a logged in character as it fetches data from ESI",
      command: "structuresearch",
    },
    {
      title: ".MARKET_STRUCTURE_ORDER",
      text: "Returns the orders in a structure in the given structure id for the type id. Optional parameter for buy/sell only. The structure must be accessible to the character signed in, and have a working market conditions.",
    },
    {
      title: ".CHARACTER_PLANETS",
      text: "Returns information about the PI planets for a character_id, viewing a single planet is done via .CHARACTER_PLANET(planet_id). If this function is run without a character id, it will return planets for all signed in characters.",
      command: ".CHARACTER_PLANETS()",
    },
  ];
  const corpData = [
    {
      title: ".CORPORATION_ASSETS",
      text: "Returns the assets for the corporation the character supplied to the function is in. Role requirement: Director. Note: 10,000 cell limit.",
    },
    {
      title: ".CORPORATION_BLUEPRINTS",
      text: "Returns the blueprints for the corporation the character supplied to the function is in. Role requirement: Director.",
    },
    {
      title: ".CORPORATION_MEMBERTRACKING",
      text: "Returns corporation members their tracking data for the corporation the character supplied to the function is in. Role requirement: Director.",
    },
    {
      title: ".CORPORATION_CONTRACTS",
      text: "Returns the contracts for the corporation the character supplied to the function is in. 10 per page. Requirement: Member of a corporation.",
    },
    {
      title: ".CORPORATION_CUSTOMS_OFFICES",
      text: "Returns the customs offices for the corporation the character supplied to the function is in. Role requirement: Director",
    },
    {
      title: ".CORPORATION_INDUSTRY_JOBS",
      text: "Returns industry jobs for the corporation the character supplied to the function is in. status_filter parameter filters the results by the status of the job (as a string) Role requirement: Factory_Manager",
    },
    {
      title: ".CORPORATION_MINING",
      text: "Returns the mining observers. Beta version.",
    },
    {
      title: ".CORPORATION_MINING_DETAILS",
      text: "Returns the details from a single mining observers. Beta version.",
    },
    {
      title: ".CORPORATION_ORDERS",
      text: "Returns the open market orders for the corporation the character supplied to the function is in. Role requirement: 'Accountant or Trader'",
    },
    {
      title: ".CORPORATION_ORDERS_HISTORY",
      text: "Returns the historical orders for the corporation the character supplied to the function is in. Role requirement: 'Accountant or Trader'",
    },
    {
      title: ".CORPORATION_WALLETS",
      text: "Returns the wallet balances and their division id for the corporation the character supplied to the function is in. Role requirement: 'Accountant or Junior_Accountant'",
    },
    {
      title: ".CORPORATION_WALLET_TRANSACTIONS",
      text: "Returns the wallet transactions for the corporation specified division. Role requirement: 'Accountant or Junior_Accountant'",
    },
    {
      title: ".CORPORATION_WALLET_JOURNAL",
      text: "Returns the wallet journal for the corporation specified division. Role requirement: 'Accountant or Junior_Accountant'",
    },
  ];
  const PublicFunctions = React.memo(() => (
    <div role="tabpanel" aria-labelledby="Public">
      <p>Public functions to static data and the EVE API.</p>
      <Accordion multiple>
        {publicData.map((item, index) => {
          return (
            <AccordionItem value={index.toString()} key={index}>
              <AccordionHeader title={item.title}>{item.title}</AccordionHeader>
              <AccordionPanel className={styles.InnerPanel}>
                <div>{item.text}</div>
                {item.command ? (
                  <Button className={styles.ButtonClick} onClick={() => injectCommandHandler(item.command)}>
                    Try it out!
                  </Button>
                ) : (
                  <></>
                )}
              </AccordionPanel>
            </AccordionItem>
          );
        })}
      </Accordion>
    </div>
  ));
  const CharacterFunctions = React.memo(() => (
    <div role="tabpanel" aria-labelledby="Character">
      <p>Requires you to have a character signed in.</p>
      <Accordion multiple>
        {characterData.map((item, index) => {
          return (
            <AccordionItem value={index.toString()} key={index}>
              <AccordionHeader title={item.title}>{item.title}</AccordionHeader>
              <AccordionPanel className={styles.InnerPanel}>
                <div>{item.text}</div>
                {item.command ? (
                  <Button className={styles.ButtonClick} onClick={() => injectCommandHandler(item.command)}>
                    Try it out!
                  </Button>
                ) : (
                  <></>
                )}
              </AccordionPanel>
            </AccordionItem>
          );
        })}
      </Accordion>
    </div>
  ));
  const CorporationFunctions = React.memo(() => (
    <div role="tabpanel" aria-labelledby="Corporation">
      <p>
        Requires a character with the proper roles for access to the data. To use these functions pass in the
        characterID or character_entity of a character in the corporation you want to use.
      </p>
      <Accordion multiple>
        {corpData.map((item, index) => {
          return (
            <AccordionItem value={index.toString()} key={index}>
              <AccordionHeader title={item.title}>{item.title}</AccordionHeader>
              <AccordionPanel className={styles.InnerPanel}>
                <div>{item.text}</div>
              </AccordionPanel>
            </AccordionItem>
          );
        })}
      </Accordion>
    </div>
  ));

  return (
    <>
      <TabList className={styles.TabList} selectedValue={selectedValue} onTabSelect={onTabSelect}>
        <Tab id="Public" value="public">
          Public
        </Tab>
        <Tab id="Character" value="character">
          Character
        </Tab>
        <Tab id="Corporation" value="corporation">
          Corporation
        </Tab>
      </TabList>
      <div className={styles.panels}>
        {selectedValue === "public" && <PublicFunctions />}
        {selectedValue === "character" && <CharacterFunctions />}
        {selectedValue === "corporation" && <CorporationFunctions />}
      </div>
    </>
  );
};
